import { createSelector } from '@reduxjs/toolkit';
import { urls } from '@ff-os/shared-utils/urls';
import { debounce } from 'lodash';
import { exportProject } from '../ExportMonitor';
import { fireFocusEventToSite } from '../global-state/site-events';
import { AppThunkApi, PdpState, PdpThunk, Reject } from '../store';
import { authorizeWithSecret } from './authorization/authorizeWithSecret';
import { createAsyncAppThunk } from './createAsyncAppThunk';
import { isProjectCompatible } from './isProjectCompatible';
import { openProject } from './openProject';

type LocalDraft<TProject> = {
    project: TProject;
    savedOn: number;
    version?: number;
    secret?: string;
};

export const loadLocalDraft = createAsyncAppThunk<void, void>(
    'drafts/load',
    async (_, thunkApi: AppThunkApi) => {

        const state = thunkApi.getState();

        const draftJson = localStorage.getItem(draftKeyOf(state));
        if (!draftJson) {
            throw new Error('Draft project JSON not found on local storage.');
        }

        const draft = JSON.parse(draftJson) as LocalDraft<unknown>;

        if (draft.secret) {
            try {
                await thunkApi.dispatch(authorizeWithSecret(draft.secret)).unwrap();
            } catch (e: any) {
                if (e.rejectType) {
                    const reject = e as Reject;
                    if (reject.rejectType === 'http' && reject.statusCode === 409) {

                        // Project already exists on server, should skip loading draft and
                        // start fresh. Existing project can be loaded by tenant.

                        thunkApi.dispatch(deleteLocalDraft());
                        return;
                    }
                    if (reject.rejectType === 'http' && reject.statusCode === 404) {

                        // Project no longer exists on the server,
                        // should skip loading draft and start fresh.

                        thunkApi.dispatch(deleteLocalDraft());
                        return;
                    }
                }

                throw e instanceof Error ? e : new Error(`Auth error: ${urls.requestExistingGrant}.`);
            }
        }

        if (draft.project) {

            const pluginId = state.scene.pluginId;
            const isCompatible = isProjectCompatible(
                state.compatibility.sceneVersion, draft.version);

            const project = { design: draft.project, savedOn: draft.savedOn };
            await openProject(thunkApi, pluginId, project, isCompatible, 'draft');
        }

        fireFocusEventToSite({ name: 'focus' });
    }
);

type SaveOptions = {
    throttle: boolean;
};

export const saveLocalDraft = createAsyncAppThunk<SaveOptions, void>(
    'drafts/save',
    async (options, thunkApi) => {

        if (options.throttle) {
            await debounce(
                async () => await saveLocalDraftToStorage(thunkApi.getState),
                1500, { leading: true, trailing: false })();
        } else {
            await saveLocalDraftToStorage(thunkApi.getState);
        }
    }
);

const saveLocalDraftToStorage = async (getState: () => PdpState) => {

    const state = getState();

    // Drafts will only be saved for projects that have yet to be pushed to the server.
    if (selectHasProjectBeenPushed(state)) {
        return;
    }

    const secret = state.auth.secret;
    const project = await exportProject();

    const json = JSON.stringify({
        project,
        secret,
        savedOn: Date.now(),
        version: state.compatibility.sceneVersion
    });

    if (json.length > 4_000_000) {
        console.warn('Local draft exceeds maximum size of 4MB.');
        return;
    }

    try {
        localStorage.setItem(draftKeyOf(state), json);
    } catch (e) {
        console.error('Failed to save local draft:', e);
    }
};

export const deleteLocalDraft = (): PdpThunk => {
    return ((_, getState) => {
        localStorage.removeItem(draftKeyOf(getState()));
    });
};

export const canLoadLocalDraft = (state: PdpState) => {
    return localStorage.getItem(draftKeyOf(state)) !== null;
};

const draftKeyOf = (state: PdpState) => `__idt_draft_${state.scene.id}`;

const selectHasProjectBeenPushed = createSelector(
    (state: PdpState) => state.snapshot.snapshotIds,
    ids => ids && ids.length > 0);